import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';
import {
  LogOut,
  User,
  BookAIcon,
  MenuIcon,
  GitBranch,
  HelpCircle,
  Sparkles,
  Calendar,
} from 'lucide-react';
import Typed from 'typed.js';
import { useTourContext } from '@gokrew/ui';
import { CommandPaletteButton } from '@/components/CommandPalette/CommandPaletteButton';
import { useMediaQuery } from '@gokrew/ui/hooks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import styles from './Header.module.css';
import { useAuth } from '@/context/AuthContext';
import { getNameFromObj, getUserInitials } from '@gokrew/ui/utility/utility';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language_Options } from '@gokrew/ui/constants/language-options';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useLinkedBranches } from '@/hooks/branches/useBranches';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { usePageHeader } from '@/context/PageHeaderContext';
import { UserMenuDropdown } from '@gokrew/ui/components/UserMenuDropdown';
import Notifications from '@/components/Notifications';
import MobileUserMenu from './MobileUserMenu';
import { AssistantHeaderButton } from '@/components/assistant';
import {
  GLOW_EASING,
  GLOW_MAX_STEP,
  GLOW_SETTLE,
  glowDistanceFor,
  glowPerimeter,
  glowPointAt,
  prefersReducedMotion,
} from '@gokrew/ui/utility/headerGlow';
import { useUpdateUserLanguage } from '@/hooks/useUpdateUserLanguage';
import { formatRoleLabel } from '@/utility';
import { UserRole } from '@/types/auth';

const WELCOME_TYPE_SPEED = 40;
const WELCOME_BACK_SPEED = 12;
const WELCOME_HOLD_MS = 5000;

const Header = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const { tours } = useTourContext();
  const { setTabsSlot } = usePageHeader();

  const {
    user,
    logout,
    branchId,
    handleSetBranch,
    selectedCompany,
    role,
    isMockLogin,
  } = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation(TranslationNS.Dashboard);
  const { t: tNav } = useTranslation(TranslationNS.sidebar);
  const [open, setOpen] = useState(false);
  // The phone layout gets its own profile panel; the desktop menu is unchanged.
  const isMobile = useMediaQuery('(max-width: 640px)');
  const welcomeRef = useRef<HTMLSpanElement>(null);

  const haloRef = useRef<HTMLSpanElement>(null);
  const glow = useRef({
    at: 0,
    to: 0,
    frame: 0,
    isEntering: true,
    width: 0,
    height: 0,
    perimeter: 0,
    originX: 0,
    originY: 0,
  });

  const runGlowFrame = useCallback(() => {
    const halo = haloRef.current;
    if (!halo) return;

    const state = glow.current;

    // Take whichever way round the pill is shorter, so crossing between the
    // edges sweeps past the nearer cap instead of cutting through the middle.
    let delta = state.to - state.at;
    if (delta > state.perimeter / 2) delta -= state.perimeter;
    if (delta < -state.perimeter / 2) delta += state.perimeter;

    const isSettled = Math.abs(delta) < GLOW_SETTLE;
    const step = Math.max(
      -GLOW_MAX_STEP,
      Math.min(GLOW_MAX_STEP, delta * GLOW_EASING)
    );
    state.at = isSettled
      ? state.to
      : (state.at + step + state.perimeter) % state.perimeter;

    const point = glowPointAt(state.at, state.width, state.height);
    // Written straight to the node rather than through state — this runs every
    // frame and must not re-render the header. Both are lengths feeding a
    // transform, so the frame costs a composite and neither layout nor paint.
    halo.style.setProperty('--glow-x', `${point.x}px`);
    halo.style.setProperty('--glow-y', `${point.y}px`);

    state.frame = isSettled ? 0 : requestAnimationFrame(runGlowFrame);
  }, []);

  const handleGlowMove = useCallback(
    (event: MouseEvent) => {
      const halo = haloRef.current;
      // The glow is hidden behind the open menu, so there is nothing to drive.
      if (!halo || open) return;

      const state = glow.current;

      // Measured once per hover rather than per event: the pill's size is fixed
      // by its content, and re-reading it after each frame's style write would
      // force a synchronous layout every frame.
      if (state.isEntering) {
        const rect = halo.getBoundingClientRect();
        state.width = rect.width;
        state.height = rect.height;
        state.perimeter = glowPerimeter(rect.width, rect.height);
        state.originX = rect.left;
        state.originY = rect.top;
      }

      // Snapped to the nearer edge rather than tracking the cursor's y: centred
      // on an edge, the glow's other half falls behind the opaque pill, so only
      // that side lights up. Dead centre resolves to the bottom.
      state.to = glowDistanceFor(
        event.clientX - state.originX,
        state.width,
        state.height,
        event.clientY - state.originY < state.height / 2
      );

      // A fresh hover starts where the cursor is instead of sweeping in from
      // wherever the last one ended. Reduced motion keeps the glow but drops
      // the travel, so every move is treated as a fresh placement.
      if (state.isEntering || prefersReducedMotion()) {
        state.isEntering = false;
        state.at = state.to;
      }

      if (!state.frame) state.frame = requestAnimationFrame(runGlowFrame);
    },
    [open, runGlowFrame]
  );

  const stopGlow = useCallback(() => {
    const state = glow.current;
    if (state.frame) cancelAnimationFrame(state.frame);
    state.frame = 0;
    // Re-measured on the next hover, which also covers a resize in between.
    state.isEntering = true;
  }, []);

  useEffect(() => stopGlow, [stopGlow]);

  const breadcrumbs = useBreadcrumbs();
  const tCrumb = (crumb: string) =>
    tNav(`nav.${crumb}`, { defaultValue: crumb });
  const pageCrumb = breadcrumbs?.pageCrumb ?? '';
  const showWelcome = !isMockLogin && !breadcrumbs;

  useEffect(() => {
    const el = welcomeRef.current;
    if (!showWelcome || !el) return;

    const strings = t('header.welcome_messages', {
      returnObjects: true,
    }) as string[];

    if (prefersReducedMotion()) {
      el.textContent = strings[0].replace(/`/g, '');
      return;
    }

    const typed = new Typed(el, {
      strings,
      contentType: 'text',
      typeSpeed: WELCOME_TYPE_SPEED,
      backSpeed: WELCOME_BACK_SPEED,
      backDelay: WELCOME_HOLD_MS,
      loop: true,
    });

    return () => typed.destroy();
  }, [showWelcome, t]);
  const currentLang = i18n.language?.trim() || 'en';
  const locale = currentLang === 'fr' ? 'fr-CA' : currentLang;
  const today = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());
  const todayShort = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date());
  const dateLine = (
    <span className={styles.welcomeDate}>
      <Calendar
        size={14}
        strokeWidth={2}
        className={styles.welcomeDateIcon}
        aria-hidden="true"
      />
      <span className="sm:hidden">
        {t('header.today', { date: todayShort })}
      </span>
      <span className="hidden sm:inline">
        {t('header.today', { date: today })}
      </span>
    </span>
  );

  const isMultiBranchEnabled = selectedCompany?.isMultiBranchEnabled === true;

  const userName = user ? getNameFromObj(user) : undefined;

  const userSubtitle = !role
    ? '---'
    : role === UserRole.SUPER_ADMIN
      ? t('header.role_super_admin')
      : role === UserRole.COMPANY_ADMIN
        ? t('header.role_company_admin')
        : formatRoleLabel(role);
  const { mutate: saveLanguage, isPending: isUpdatingLanguage } =
    useUpdateUserLanguage();

  const { data: branchesData } = useLinkedBranches({
    enabled: !!isMultiBranchEnabled,
  });
  const branches = branchesData?.data ?? [];

  const branchSubItems = [
    { code: 'all', label: t('header.all_branches') },
    ...branches.map((b) => ({
      code: b.id,
      label: b.name,
    })),
  ];
  const showBranchPicker = isMultiBranchEnabled && branches.length > 1;

  const handleLanguageSelect = (code: string) => {
    if (isUpdatingLanguage) return;

    i18n.changeLanguage(code);

    saveLanguage(code);
  };
  const handleBranchSelect = (id: string) =>
    handleSetBranch(id === 'all' ? null : id);

  return (
    <>
      {open && <div className={styles.menuOverlay} aria-hidden="true" />}
      <header
        className={`${styles.header} ${open ? styles.headerMenuOpen : ''}`}
      >
        <div className={styles.headerRow}>
          <div className="flex gap-4 items-center min-w-0 max-[400px]:gap-2">
            <button
              className={styles.menuBtn}
              onClick={onMenuClick}
              aria-label={t('header.open_menu')}
            >
              <MenuIcon size={22} />
            </button>
            <div className={styles.welcome}>
              {isMockLogin ? (
                <h1 className={styles.welcomeTitle}>
                  {t('header.impersonating_as', {
                    name: user?.firstName || 'User',
                  })}
                </h1>
              ) : breadcrumbs ? (
                <>
                  <h1 className={styles.welcomeTitle}>{tCrumb(pageCrumb)}</h1>
                  {!breadcrumbs.crumbs[1] ? (
                    dateLine
                  ) : (
                    <div className={styles.breadcrumbs}>
                      <span
                        className={`${styles.breadcrumbSection} cursor-pointer`}
                        onClick={() => navigate(breadcrumbs.rootPath)}
                      >
                        {tCrumb(breadcrumbs.crumbs[0])}
                      </span>
                      <span className={styles.breadcrumbSeparator}>•</span>
                      {breadcrumbs.crumbs[2] ? (
                        <>
                          <span
                            className={`${styles.breadcrumbSection} cursor-pointer`}
                            onClick={() =>
                              breadcrumbs.parentPath &&
                              navigate(breadcrumbs.parentPath)
                            }
                          >
                            {tCrumb(breadcrumbs.crumbs[1])}
                          </span>
                          <span className={styles.breadcrumbSeparator}>•</span>
                          <span className={styles.breadcrumbPage}>
                            {tCrumb(breadcrumbs.crumbs[2])}
                          </span>
                        </>
                      ) : (
                        <span className={styles.breadcrumbPage}>
                          {tCrumb(breadcrumbs.crumbs[1])}
                        </span>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <h1 className={styles.welcomeTitle}>
                    <span className="max-sm:hidden">
                      {t('header.welcome_user', {
                        name: user?.firstName || 'User',
                      })}{' '}
                      <span ref={welcomeRef} />
                    </span>
                    <span className="sm:hidden">{t('header.welcome')}</span>
                  </h1>
                  {dateLine}
                </>
              )}
            </div>
          </div>
          <div className={styles.rightGroup}>
            <CommandPaletteButton className={styles.tourBtn} />
            <AssistantHeaderButton
              className={`${styles.tourBtn} ${styles.assistantBtn}`}
            />
            <div className={styles.notificationsSlot}>
              <Notifications />
            </div>
            {tours.length === 1 && (
              <button
                type="button"
                className={styles.tourBtn}
                onClick={tours[0].start}
                aria-label={tours[0].label}
                title={tours[0].label}
              >
                <HelpCircle size={18} />
              </button>
            )}
            {tours.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={styles.tourBtn}
                    aria-label={t('header.tour_menu')}
                    title={t('header.tour_menu')}
                  >
                    <HelpCircle size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-56">
                  {tours.map((tour) => (
                    <DropdownMenuItem key={tour.id} onSelect={tour.start}>
                      {tour.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {tours.length > 0 && (
              <span className={styles.separator} aria-hidden="true" />
            )}
            <div
              className={`${styles.actions} ${open ? styles.actionsOpen : ''}`}
              onMouseMove={handleGlowMove}
              onMouseLeave={stopGlow}
            >
              <span
                className={styles.glowHalo}
                ref={haloRef}
                aria-hidden="true"
              >
                <span className={styles.glow} />
              </span>
              {isMobile ? (
                <MobileUserMenu
                  initials={getUserInitials(user!)}
                  name={userName}
                  subtitle={userSubtitle}
                  open={open}
                  onOpenChange={setOpen}
                  triggerClassName={`${styles.userMenuTrigger} ${styles.userMenuTriggerMobile}`}
                  avatarClassName={styles.avatar}
                  chevronClassName={styles.userMenuChevron}
                  currentLang={currentLang}
                  onLanguageSelect={handleLanguageSelect}
                  branches={branchSubItems}
                  activeBranchCode={branchId ?? 'all'}
                  showBranches={showBranchPicker}
                  onBranchSelect={handleBranchSelect}
                  onProfile={() => navigate('/profile')}
                  onReleaseNotes={() => navigate('/release-notes')}
                  onLogout={logout}
                />
              ) : (
                <UserMenuDropdown
                  imageSrc={null}
                  initials={getUserInitials(user!)}
                  avatarClassName={styles.avatar}
                  triggerLabel={t('header.user_menu')}
                  name={userName}
                  subtitle={userSubtitle}
                  avatarPosition="start"
                  showChevron
                  triggerClassName={styles.userMenuTrigger}
                  nameClassName={styles.userName}
                  subtitleClassName={styles.userRole}
                  chevronClassName={styles.userMenuChevron}
                  open={open}
                  onOpenChange={setOpen}
                  contentClassName={styles.dropdownContent}
                  itemClassName={styles.dropdownItem}
                  dangerItemClassName={`${styles.dropdownItem} ${styles.dropdownItemDanger}`}
                  items={[
                    {
                      label: t('header.profile'),
                      icon: User,
                      onSelect: () => navigate('/profile'),
                    },
                    {
                      label: t('header.whats_new'),
                      icon: Sparkles,
                      onSelect: () => navigate('/release-notes'),
                    },
                    {
                      type: 'submenu',
                      label: t('header.branch'),
                      icon: GitBranch,
                      hidden: !showBranchPicker,
                      subItems: branchSubItems,
                      activeSubItemCode: branchId ?? 'all',
                      onSubItemSelect: handleBranchSelect,
                    },
                    {
                      type: 'submenu',
                      label: t('header.languages'),
                      icon: BookAIcon,
                      subItems: Language_Options,
                      activeSubItemCode: currentLang,
                      onSubItemSelect: handleLanguageSelect,
                    },
                    {
                      label: t('header.logout'),
                      icon: LogOut,
                      variant: 'danger',
                      dividerBefore: true,
                      onSelect: logout,
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>
        <div ref={setTabsSlot} className={styles.tabsSlot} />
      </header>
    </>
  );
};

export default memo(Header);
